/**
 * DealQuickCreateDialog - Minimal dialog for quick deal creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3-5 fields maximum (Name, Pipeline/Stage, Organization)
 * - Redirects to detail page after creation for full editing
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { CompanyPicker, DialogForm, MonetaryAmountInput } from '../../../shared/components/common';
import { useCreateDeal } from '../hooks/useDeals';
import { useApiError } from '../../../shared/hooks/useApiError';
import { usePipelines } from '../hooks/usePipelines';
import { useLastUsedPipeline } from '../hooks/useLastUsedPipeline';
import { useTenantLocaleSettings } from '../../../shared/hooks/useTenantSettings';
import { OrganizationPicker, ContactPicker } from '../../master_data/contacts';
import { PipelinePicker } from './PipelinePicker';
import type { Deal } from '../types';

interface DealQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill name (e.g., "Copy of ...") */
  defaultName?: string;
  /** Pre-fill amount in dollars (display unit, not cents) */
  defaultAmount?: number | null;
  /** Pre-fill currency code */
  defaultCurrency?: string;
  /** Pre-fill company from context */
  defaultCompanyId?: string;
  /** Pre-fill stage from context (e.g., from kanban column) */
  defaultStageId?: string;
  /** Callback when deal is created (before navigation) */
  onCreated?: (deal: Deal) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
}

interface QuickCreateFormData {
  name: string;
  pipeline_id: string;
  stage_id: string;
  organization_id?: string;
  primary_contact_id?: string;
  amount?: number | null;
  currency?: string;
  company_id?: string | null;
}

export function DealQuickCreateDialog({
  open,
  onClose,
  defaultName,
  defaultAmount,
  defaultCurrency: defaultCurrencyProp,
  defaultCompanyId,
  defaultStageId,
  onCreated,
  skipNavigation = false,
}: DealQuickCreateDialogProps) {
  const { t } = useTranslation(['pipeline', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateDeal();
  const { handleMutationError } = useApiError({ showToast: false });
  const { data: pipelinesData } = usePipelines();
  const { data: tenantSettings } = useTenantLocaleSettings();
  const { lastPipelineId } = useLastUsedPipeline();
  const defaultCurrency = defaultCurrencyProp || tenantSettings?.default_currency || 'USD';

  // Find default pipeline and stage. Prefer the pipeline used for the last
  // created deal (if it still exists), then the tenant default, then the first.
  const defaultPipeline = useMemo(() => {
    if (!pipelinesData) return undefined;
    const remembered = lastPipelineId
      ? pipelinesData.find((p) => p.id === lastPipelineId)
      : undefined;
    return remembered || pipelinesData.find((p) => p.is_default) || pipelinesData[0];
  }, [pipelinesData, lastPipelineId]);

  const selectedPipelineId = defaultStageId
    ? pipelinesData?.find((p) => p.stages?.some((s) => s.id === defaultStageId))?.id
    : defaultPipeline?.id;

  const defaultStage = useMemo(() => {
    if (defaultStageId) {
      for (const pipeline of pipelinesData || []) {
        const stage = pipeline.stages?.find((s) => s.id === defaultStageId);
        if (stage) return stage;
      }
    }
    return defaultPipeline?.stages?.[0];
  }, [pipelinesData, defaultPipeline, defaultStageId]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      name: defaultName || '',
      pipeline_id: selectedPipelineId || '',
      stage_id: defaultStage?.id || '',
      organization_id: defaultCompanyId || undefined,
      primary_contact_id: undefined,
      amount: defaultAmount ?? null,
      currency: defaultCurrency,
      company_id: null,
    },
  });

  // B2C pipelines lead with the individual contact and collapse the organization.
  const [showOrganization, setShowOrganization] = useState(false);

  // Track whether the user has typed their own name. Until they do, selecting a
  // customer (organization for B2B, contact for B2C) seeds a default like
  // "ACME deal" / "Fred Smith deal" so most deals never need a manual name.
  // A caller-supplied defaultName (e.g. "Copy of …") counts as user intent.
  const nameEditedRef = useRef(Boolean(defaultName));

  // Seed the form exactly once per open. `pipelinesData` loads async, so
  // `defaultPipeline` is undefined on the first render(s) after open — we must
  // wait for it before seeding pipeline/stage, but once seeded we must NOT
  // re-seed when that data lands, or we'd wipe a name the user typed in the
  // meantime (the "This field is required" race).
  const formInitializedRef = useRef(false);

  const applyDefaultName = useCallback(
    (customerName: string | null | undefined) => {
      const trimmed = customerName?.trim();
      if (!trimmed || nameEditedRef.current) return;
      // shouldValidate clears any stale "required" error now that the seeded
      // name is a valid value — selecting a customer must not leave a red error.
      setValue('name', t('deals.defaultNameFromCustomer', { name: trimmed }), {
        shouldValidate: true,
      });
    },
    [setValue, t],
  );

  const watchedPipelineId = watch('pipeline_id');
  const watchedStageId = watch('stage_id');
  const watchedCompanyId = watch('company_id');

  // Get stages for selected pipeline
  const selectedPipeline = useMemo(() => {
    return pipelinesData?.find((p) => p.id === watchedPipelineId);
  }, [pipelinesData, watchedPipelineId]);

  // B2C pipelines make the individual contact the headline association and
  // collapse the organization; B2B keeps the organization first-class.
  const isB2C = selectedPipeline?.customer_segment === 'b2c';

  useEffect(() => {
    if (!open) {
      // Reset the one-shot guards on close so the next open re-seeds cleanly.
      formInitializedRef.current = false;
      nameEditedRef.current = Boolean(defaultName);
      return;
    }
    if (formInitializedRef.current || !defaultPipeline) return;
    formInitializedRef.current = true;
    // Preserve a name the user typed while pipelinesData was still loading —
    // re-seeding it to the default here is what caused the "required" race.
    const preservedName = nameEditedRef.current ? getValues('name') : defaultName || '';
    reset({
      name: preservedName,
      pipeline_id: selectedPipelineId || defaultPipeline.id,
      stage_id: defaultStage?.id || defaultPipeline.stages?.[0]?.id || '',
      organization_id: defaultCompanyId || undefined,
      primary_contact_id: undefined,
      amount: defaultAmount ?? null,
      currency: defaultCurrency,
      company_id: null,
    });
    setShowOrganization(false);
  }, [open, defaultPipeline, defaultStage, selectedPipelineId, defaultCompanyId, defaultName, defaultAmount, defaultCurrency, reset, getValues]);

  // Won-stage company gate: when the selected stage is a 'won' stage,
  // company_id is required to drive deal-to-invoice generation.
  const selectedStageIsWon = useMemo(() => {
    const stage = selectedPipeline?.stages?.find((s) => s.id === watchedStageId);
    return stage?.stage_type === 'won';
  }, [selectedPipeline, watchedStageId]);

  // Reset stage when pipeline changes
  useEffect(() => {
    if (selectedPipeline && !defaultStageId) {
      const firstStage = selectedPipeline.stages?.[0];
      if (firstStage) {
        setValue('stage_id', firstStage.id);
      }
    }
  }, [watchedPipelineId, selectedPipeline, defaultStageId, setValue]);

  // Pipeline/stage are seeded asynchronously from `usePipelines`. Until they land
  // the form's required pipeline_id/stage_id are empty, so a submit can only fail
  // validation — and those two controls surface that as a bare red border, i.e. a
  // Create click that silently does nothing. Gate the primary action on them
  // instead: the button stays disabled while the pipelines query is in flight (and
  // permanently, if the tenant has no pipeline at all, which is the honest state).
  const canSubmit = Boolean(watchedPipelineId && watchedStageId);

  const onSubmit = async (data: QuickCreateFormData) => {
    try {
      const newDeal = await createMutation.mutateAsync({
        name: data.name,
        pipeline_id: data.pipeline_id,
        stage_id: data.stage_id,
        organization_id: data.organization_id || undefined,
        primary_contact_id: data.primary_contact_id || undefined,
        company_id: data.company_id || undefined,
        ...(data.amount != null ? {
          amount: data.amount,
          currency_code: data.currency || defaultCurrency,
        } : {}),
      });
      onCreated?.(newDeal);
      onClose();

      if (!skipNavigation) {
        navigate(`/pipeline/deals/${newDeal.id}`);
      }
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('deals.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isSubmitting}
      isDisabled={!canSubmit}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('deals.pipeline')} *</Label>
            <Controller
              name="pipeline_id"
              control={control}
              rules={{ required: t('common:validation.required') }}
              render={({ field }) => (
                <PipelinePicker
                  value={field.value}
                  onChange={field.onChange}
                  onPipelineSelected={(pipeline) => {
                    // When a pipeline is created/selected inline, land the deal
                    // in its first stage so the Stage select isn't left empty.
                    const firstStage = pipeline?.stages?.[0];
                    if (firstStage) setValue('stage_id', firstStage.id);
                  }}
                  placeholder={t('common:app.select')}
                  error={!!errors.pipeline_id}
                />
              )}
            />
            {errors.pipeline_id && (
              <p className="text-sm text-destructive">{errors.pipeline_id.message}</p>
            )}
          </div>

          {/* Stage stays a plain select scoped to the chosen pipeline — documented
              exception to the inline-create policy (a stage's position/type/
              probability/gate-criteria are funnel design owned by the Pipelines
              page; a bare inline stage mid-deal would be malformed). */}
          <div className="space-y-2">
            <Label>{t('deals.stage')} *</Label>
            <Controller
              name="stage_id"
              control={control}
              rules={{ required: t('common:validation.required') }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    className={cn(errors.stage_id && 'border-destructive')}
                    data-testid="deal-stage-select"
                  >
                    <SelectValue placeholder={t('common:app.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedPipeline?.stages?.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.stage_id && (
              <p className="text-sm text-destructive">{errors.stage_id.message}</p>
            )}
          </div>
        </div>

        {isB2C ? (
          <>
            {/* B2C: the individual contact is the headline association — selecting
                it seeds the default deal name. */}
            <div className="space-y-2">
              <Label>{t('deals.contact')}</Label>
              <Controller
                name="primary_contact_id"
                control={control}
                render={({ field }) => (
                  <ContactPicker
                    value={field.value || ''}
                    onChange={(id) => field.onChange(id || undefined)}
                    onContactSelected={(contact) =>
                      applyDefaultName(
                        contact ? `${contact.first_name} ${contact.last_name}` : null,
                      )
                    }
                    allowClear
                    placeholder={t('deals.contactPlaceholder')}
                  />
                )}
              />
            </div>
            {/* Organization is optional for a consumer deal — collapsed by default. */}
            {showOrganization ? (
              <div className="space-y-2">
                <Label>{t('deals.company')}</Label>
                <Controller
                  name="organization_id"
                  control={control}
                  render={({ field }) => (
                    <OrganizationPicker
                      value={field.value || ''}
                      onChange={(id) => field.onChange(id || undefined)}
                      allowClear
                      defaultRelationships={['lead', 'prospect']}
                    />
                  )}
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="inline-flex items-center text-xs text-primary hover:underline cursor-pointer h-auto p-0"
                onClick={() => setShowOrganization(true)}
              >
                <Plus className="h-3.5 w-3.5 me-1" />
                {t('deals.addOrganization')}
              </Button>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <Label>{t('deals.company')}</Label>
            <Controller
              name="organization_id"
              control={control}
              render={({ field }) => (
                <OrganizationPicker
                  value={field.value || ''}
                  onChange={(id) => field.onChange(id || undefined)}
                  onCompanySelected={(company) => applyDefaultName(company?.name)}
                  allowClear
                  defaultRelationships={['lead', 'prospect']}
                />
              )}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="name">{t('deals.name')} *</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="name"
                onChange={(e) => {
                  nameEditedRef.current = true;
                  field.onChange(e);
                }}
                placeholder={t('deals.namePlaceholder')}
                className={cn(errors.name && 'border-destructive')}
              />
            )}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <MonetaryAmountInput
          currency={watch('currency') || defaultCurrency}
          amount={watch('amount')}
          onCurrencyChange={(cur) => setValue('currency', cur || defaultCurrency)}
          onAmountChange={(amt) => setValue('amount', amt)}
          amountLabel={t('deals.amount')}
          size="sm"
        />

        <Controller
          name="company_id"
          control={control}
          rules={{
            validate: (v) =>
              !selectedStageIsWon || !!v || (t('deals.companyRequiredForWon', 'A {{company}} is required when the deal stage is Won') as string),
          }}
          render={({ field }) => (
            <CompanyPicker
              value={field.value ?? null}
              onChange={field.onChange}
              label={t('deals.dealCompany', 'Booking {{company}}') as string}
              required={selectedStageIsWon}
              showWhenSingle={selectedStageIsWon && !watchedCompanyId}
            />
          )}
        />
        {errors.company_id && (
          <p className="text-sm text-destructive">{errors.company_id.message as string}</p>
        )}

        <p className="text-xs text-muted-foreground">
          {t('deals.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
